// A step off the hero's background, so the section reads as its own band. No padding above or
// below: the feed runs the band's full height.
.section {
  position: relative;
  padding: 0 2rem;
  scroll-margin-top: calc(4.5rem + env(safe-area-inset-top));
  border-block: 1px solid var(--color-border);
  background: light-dark(#fafafe, var(--color-surface-subtle));
}

.backdrop {
  --product-dot-opacity: 0.3;
  // Match the world map’s 2.9px round strokes.
  --product-dot-radius: 1.45px;
  position: absolute !important;
  inset: 0;
}

.container {
  position: relative;
  z-index: 1;
  max-width: 74rem;
  margin: 0 auto;
}

// The feed on the left, flush with the band's top and bottom; the title and the latest verdict on
// the right.
.demo {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 4rem;
  align-items: stretch;
}

.connector {
  position: absolute;
  z-index: 1;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;

  path {
    fill: none;
    stroke: var(--color-primary);
    stroke-width: 2;
    stroke-opacity: 0.65;
  }
}

.connectorEnd {
  fill: white;
  stroke: var(--color-primary);
  stroke-width: 2;
  stroke-opacity: 1;
  animation: connectorFade 300ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
}

@keyframes connectorFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* The stamped feed */

.feed {
  position: relative;
  min-width: 0;
  min-height: 32rem;
}

// Posts drift through the full band; wheel and touch still scroll the page.
.feedViewport {
  position: absolute;
  inset: 0;
  overflow: clip;
}

// The composer stays below the feed while the posts move above it.
.feedColumn {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 1rem;

  .feed { flex: 1; }
}

.composer {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-card);
  box-shadow: var(--shadow-sm);
}

.composer .composerInput {
  width: 100%;
  min-width: 0;
  padding: 0.25rem 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  resize: vertical;
}

.composerFooter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--color-border);
}

.calmgramLogo {
  font-family: "Snell Roundhand", "Brush Script MT", "Segoe Script", cursive;
  font-size: 1.25rem;
  font-weight: 600;
}

.feedTrack {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 4px 0;
  will-change: transform;
}

.post {
  transition:
    border-color 300ms ease,
    box-shadow 300ms ease,
    opacity 2400ms ease;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.125rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-card);
  box-shadow: var(--shadow-xs);
  opacity: 0.4;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

// Focus the next pending post; completed posts lose their glow and slowly fade.
.post[data-shown] {
  opacity: 1;
  transition-duration: 300ms, 300ms, 250ms;
}

.post[data-connected] {
  border-color: var(--color-primary);
  opacity: 1;
}

.post[data-shown]:not([data-state="stamped"]) {
  animation: pendingPulse 1600ms ease-in-out infinite;

  .demo[data-moving="false"] & {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
}

@keyframes pendingPulse {
  0%, 100% {
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-primary) 8%, transparent);
  }
  50% {
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-primary) 22%, transparent);
  }
}

.postAuthor {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  strong {
    display: block;
    font-size: 0.875rem;
  }
}

.postLabel {
  display: block;
  color: var(--color-muted);
  font-size: 0.75rem;
}

// Clip the blur to the content so it cannot bleed into the card.
.postBody {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-md);

  &[data-concealed="true"] {
    display: grid;

    .postContent { grid-area: 1 / 1; }
  }

  &[data-hidden="false"] .hiddenCover {
    visibility: hidden;
  }

  &[data-hidden="true"] .postContent {
    filter: blur(5px);
    opacity: 0.5;
    pointer-events: none;
    user-select: none;
  }
}

.postContent {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.postText {
  margin: 0;
  color: var(--color-foreground);
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.45;
  text-wrap: pretty;
}

.postMedia {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 600 / 320;
  object-fit: cover;
  overflow: hidden;
  border-radius: var(--radius-md);
}

.hiddenCover {
  position: relative;
  grid-area: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: color-mix(in srgb, var(--color-card) 35%, transparent);
}

.hiddenLabel {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-card);
  color: var(--color-foreground);
  font-size: 0.8125rem;
  font-weight: 600;
  text-align: center;

  svg {
    flex-shrink: 0;
  }
}

// The verdict stamped under a post: its outcome and why, then the eight category scores with the
// deciding one named. Until the post is checked it is invisible, holding its space.
// One line under a post: the deciding category's ring-pill and the context behind the verdict.
// It holds its height while the post waits, so the feed never jumps.
.stamp {
  display: flex;
  min-height: 2rem;
  align-items: center;
  gap: 0.625rem;
}

@keyframes stamp {
  from {
    opacity: 0;
    transform: scale(1.04);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.stampVerdict {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem 0.625rem;
}

.stampReason {
  color: var(--color-muted);
  font-size: 0.8125rem;

  .stamp[data-state="checking"] & {
    color: var(--color-primary);
    font-weight: 500;
  }
}

// Stretched over the whole post: pressing a post pins it beside the feed.
.post {
  position: relative;
}

.postPin {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
  }
}

.indicators {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  margin: 0;
  padding: 0;
  list-style: none;
  justify-content: flex-end;
}

.indicator {
  display: flex;
  align-items: center;
  color: var(--color-muted-foreground);
}

.postHeader {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;

  > :last-child {
    margin-inline-start: auto;
  }
}

.postDetail {
  margin: -1rem -1rem 0;
}

.decisionExplanation {
  font-size: 1rem;
  line-height: 1.5;
  font-weight: 500;
}

.verdictGroup {
  min-width: 0;
}


.verdictFooter {
  margin: 0 -1rem -1rem;
  padding: 1rem;
  border-top: 1px solid var(--color-border);

  .indicators { justify-content: center; }
}

/* The title and the latest verdict */

.panel {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2rem;
  padding-block: 3rem;


}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.5rem;
}

.guide {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
}

.header {
  order: -2;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.title {
  margin: 0;
}

// Reserve space for changing verdicts while the card fits its own content.
.verdictSlot {
  display: grid;
  min-height: 32rem;
  align-items: center;
  min-width: 0;
}

// The post the feed last stamped, and the score behind its verdict.
.shown {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 1rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-card);
  box-shadow: var(--shadow-sm);
  animation: shownIn 300ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
}

.watermark {
  position: absolute;
  top: 1.25rem;
  inset-inline-end: 1rem;
  font-size: 1.25rem;
  color: var(--color-muted);
  opacity: 0.45;
  pointer-events: none;
}

@keyframes shownIn {
  from {
    opacity: 0;
    transform: translateY(0.375rem);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (max-width: 1000px) {
  .connector { display: none; }
  .demo {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }

  // Stacked: the title first, then the feed, then the latest verdict.
  .panel {
    display: contents;
  }

  .header {
    position: relative;
    z-index: 1;
    order: -1;
    padding-top: 2rem;
    padding-bottom: 1rem;

    .title {
      position: relative;
    }
  }

  .feedViewport {
    mask-image: linear-gradient(to bottom, transparent, black 3rem);
  }

  .feed {
    min-height: 26rem;
    height: 26rem;
  }

  // Keep the card touching the feed above.
  .verdictSlot {
    min-height: 0;
    align-items: start;

    &[data-visible="true"] {
      transition: height 350ms ease;
    }

    @media (prefers-reduced-motion: reduce) {
      &[data-visible="true"] { transition: none; }
    }

    position: relative;
    z-index: 1;
  }

  .shown {
    margin-inline: -0.5rem;
  }

  .composer {
    margin-top: 1rem;
  }

  .actions {
    margin-block: 2rem 4.5rem;
  }
}

@media (max-width: 640px) {
  .section {
    padding: 0 1rem;
  }

}
